import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react'
import QRCode from 'qrcode';
import DropdownEditMenu from '../../../components/Dashboard/DropdownEditMenu'
import { Link, useParams } from 'react-router-dom'
import LoadContentSpinner from '../../../components/Utils/LoadContentSpinner';
import axiosClient from '../../../components/api/axiosClient';
import FormattedDate from '../../../components/Utils/FormattedDate';
import { formatPrice } from '../../../components/Utils/UtilsPricesFormat';
import EventsRecapOrdersComponent from '../../../components/Dashboard/eventsContent/EventsRecapOrdersComponent';
import DoEventActions from '../../../components/Dashboard/eventsContent/DoEventActions';
import { formatNumber, formatPerformNumber } from '../../../components/Utils/UtilsNumbersFormat';
import ShareEventModal from '../../../components/ShareEventModal';
import CommentPassShow from '../../../components/Dashboard/DropdownShowPassComment';
import ModalDeletePassComment from '../../../components/ModalDeletePassComment';
import ModalEditPassComment from '../../../components/ModalEditPassComment';
import { buildEventBookingUrl } from '../../../config/billetterie';
import DashboardPromoMarker from '../../../components/Utils/DashboardPromoMarker';
import PromoEventBadge from '../../../components/Utils/PromoEventBadge';
import PassSessionTabs from '../../../components/Dashboard/PassSessionTabs';
import SessionBadge from '../../../components/Dashboard/SessionBadge';
import {
    isMultiSessionEvent,
    resolveInitialTicketTab,
    ticketsForTab,
} from '../../../utils/sessionTickets';

/** Lien d'achat public : priorité aux champs API, sinon URL billetterie Laravel (/events/{id}/paiement). */
function resolvePurchaseLink(eventData, payload) {
    const raw =
        eventData.lien_achat ??
        payload?.lien_achat ??
        null;

    if (raw != null && String(raw).trim() !== '') {
        const s = String(raw).trim();
        if (/^https?:\/\//i.test(s)) return s;
        if (s.startsWith('/') && typeof window !== 'undefined') {
            return `${window.location.origin}${s}`;
        }
        return s;
    }

    return buildEventBookingUrl(eventData.id);
}

/**
 * Logo rond au centre du QR (aligné sur QrCodeSection : 40×40 pour un QR de 220px de côté).
 * Si le logo ne charge pas ou bloque l’export (CORS), la promesse résout à false.
 */
function drawRoundedLogoOnQrCanvas(canvas, logoSrc) {
    return new Promise((resolve) => {
        if (!logoSrc || typeof document === 'undefined') {
            resolve(false);
            return;
        }
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
            try {
                const ctx = canvas.getContext('2d');
                if (!ctx) {
                    resolve(false);
                    return;
                }
                const w = canvas.width;
                const h = canvas.height;
                const d = Math.round((Math.min(w, h) * 40) / 220);
                const r = d / 2;
                const cx = w / 2;
                const cy = h / 2;
                const pad = Math.max(2, Math.round(d * 0.08));

                ctx.save();
                ctx.beginPath();
                ctx.arc(cx, cy, r + pad, 0, Math.PI * 2);
                ctx.fillStyle = '#ffffff';
                ctx.fill();
                ctx.restore();

                ctx.save();
                ctx.beginPath();
                ctx.arc(cx, cy, r, 0, Math.PI * 2);
                ctx.closePath();
                ctx.clip();
                ctx.drawImage(img, cx - r, cy - r, d, d);
                ctx.restore();
                resolve(true);
            } catch {
                resolve(false);
            }
        };
        img.onerror = () => resolve(false);
        img.src = logoSrc;
    });
}

const PURCHASE_QR_JPEG_OPTS = {
    width: 480,
    margin: 2,
    errorCorrectionLevel: 'H',
    color: { dark: '#000000', light: '#ffffff' },
};

export default function ViewDetailsToEvents() {

    const [activeTab, setActiveTab] = useState('general'); // État pour gérer l'onglet actif
    const { id } = useParams();  // Récupérer l'ID de l'événement à partir de l'URL
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [details, setDetails] = useState(null);
    const [isShareModalOpen, setShareModalOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [description, setDescription] = useState('');
    const [loadingBtnDesc, setLoadingBtnDesc] = useState(false);
    const [errorsDesc, setErrorsDesc] = useState('');
    const [validationError, setValidationError] = useState('');
    const [successMessage, setSuccessMessage] = useState();
    const [isDeletePassModalOpen, setDeletePassModalOpen] = useState(false);
    const [isEditPassModalOpen, setEditPassModalOpen] = useState(false);
    const [selectedPass, setSelectedPass] = useState(null);
    const [animatedViewsCount, setAnimatedViewsCount] = useState(0);
    const [copiedPurchaseLink, setCopiedPurchaseLink] = useState(false);
    const [downloadingPurchaseQr, setDownloadingPurchaseQr] = useState(false);
    const [togglingShowNbrPivotId, setTogglingShowNbrPivotId] = useState(null);
    const [purchaseActionsOpen, setPurchaseActionsOpen] = useState(false);
    const [isPosterLightboxOpen, setPosterLightboxOpen] = useState(false);
    const [activeTicketTab, setActiveTicketTab] = useState(null);
    const purchaseActionsRef = useRef(null);

    const isShowNbrEnabled = (pivot) =>
        pivot?.show_nbr === true || pivot?.show_nbr === 1 || pivot?.show_nbr === '1';

    // Fonction pour changer l'onglet actif
    const handleTabClick = (tab) => {
        setActiveTab(tab);
    };

    const fetchEventDetails = async () => {
        setError(null);   // Réinitialiser les erreurs
        // Appel API pour récupérer les événements en cours de validation
        try {
            const response = await axiosClient.get(`/dashboard/event/details/e/${id}`);
            if (response.data.noContent) {
                setError(response.data); // Afficher "Aucun résultat trouvé."
            } else {
                const payload = response.data || {};
                const eventData = payload.data || {};
                // console.log(response.data);

                // Vues cumulées : l'API renvoie le total dans data.tracking.total_event_views
                const tracking = eventData.tracking || {};
                const normalizedViewsCount = Number(
                    tracking.total_event_views ??
                    eventData.total_event_views ??
                    payload.data?.tracking?.total_event_views ??
                    payload.total_event_views ??
                    0
                ) || 0;

                setDetails({
                    ...eventData,
                    total_views: normalizedViewsCount,
                    purchase_link: resolvePurchaseLink(eventData, payload),
                });
                setDescription(eventData.descrip_apk || ''); // Met à jour la description après récupération
            }
        } catch (error) {
            if (error.response && error.response.data) {
                // Récupérer les messages spécifiques de l'erreur backend
                setError(error.response.data);
            } else {
                setError({message: 'Une erreur inconnue est survenue. Veuillez réessayer plus tard.',});
            }
        } finally {
            setLoading(false); // Désactiver le spinner
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        
        if (!description.trim()) {
            setErrorsDesc('La description ne peut pas être vide.'); // Message d'erreur
            return;
        }
        setErrorsDesc('')
        setLoadingBtnDesc(true); // Indique que le bouton est en cours de traitement

        axiosClient.post(`/dashboard/event/details/update/description/e/${id}`, {descrip_apk: description})
            .then(
                ({data}) => {
                    setSuccessMessage(data.message);
                    fetchEventDetails();
                    setIsEditing(false); // Quitte le mode édition
                    setLoadingBtnDesc(false);
                    setTimeout(() => setSuccessMessage(""), 4000);

            }).catch(err => {
                const response = err.response;
                if (response) {
                    // console.log("Erreur de l'API :", response);
                    if (response.status === 422) {
                        setValidationError("Erreur ! Veuillez vérifier vos informations puis réessayer s'il vous plait !");
                    }else if (response.status === 500) {
                        if (response.data.errors) {
                            // Erreurs spécifiques aux champs
                            setErrorsDesc(response.data.errors);
                        }
                        if (response.data.message) {
                            // Erreur générale
                            setValidationError(response.data.message);
                        }
                    }
                    setLoadingBtnDesc(false);
                    setTimeout(() => setValidationError(""), 7000);
                } else {
                    setValidationError("Erreur lors de la sauvegarde.");
                    setLoadingBtnDesc(false);
                    setTimeout(() => setValidationError(""), 7000);
                }
            })
    };

    const handleCancel = () => {
        setDescription(details.descrip_apk); // Réinitialiser à la valeur initiale
        setIsEditing(false); // Quitter le mode édition
        setErrorsDesc('')
    };

    useEffect(() => {
        if (id) {
            fetchEventDetails();
        }
    }, [id]);

    useEffect(() => {
        if (!purchaseActionsOpen) return undefined;
        const onClick = (e) => {
            if (purchaseActionsRef.current && !purchaseActionsRef.current.contains(e.target)) {
                setPurchaseActionsOpen(false);
            }
        };
        document.addEventListener('click', onClick);
        return () => document.removeEventListener('click', onClick);
    }, [purchaseActionsOpen]);

    useEffect(() => {
        if (!isPosterLightboxOpen) return undefined;
        const onKeyDown = (e) => {
            if (e.key === 'Escape') setPosterLightboxOpen(false);
        };
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.body.style.overflow = prevOverflow;
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [isPosterLightboxOpen]);

    useEffect(() => {
        const targetViews = Number(details?.total_views ?? 0) || 0;

        // Petite animation du compteur pour un affichage plus dynamique.
        let start = 0;
        const duration = 900;
        const stepTime = 16;
        const increment = Math.max(1, Math.ceil(targetViews / (duration / stepTime)));

        const timer = setInterval(() => {
            start += increment;
            if (start >= targetViews) {
                setAnimatedViewsCount(targetViews);
                clearInterval(timer);
                return;
            }
            setAnimatedViewsCount(start);
        }, stepTime);

        return () => clearInterval(timer);
    }, [details]);

    const handleShareClick = () => {
        setShareModalOpen(true);
    };

    const handleCopyPurchaseLink = (link) => {
        if (!link) return;
        navigator.clipboard.writeText(link).then(
            () => {
                setCopiedPurchaseLink(true);
                setTimeout(() => setCopiedPurchaseLink(false), 3000);
            },
            () => {
                setValidationError('Impossible de copier le lien.');
                setTimeout(() => setValidationError(''), 5000);
            },
        );
    };

    /** Export JPEG avec logo rond au centre (même asset que la home / QrCodeSection). */
    const handleDownloadPurchaseQr = useCallback(async () => {
        const link = details?.purchase_link;
        if (!link) return;
        setDownloadingPurchaseQr(true);
        try {
            const canvas = document.createElement('canvas');
            await QRCode.toCanvas(canvas, link, PURCHASE_QR_JPEG_OPTS);

            const imagePath = process.env.REACT_APP_IMAGE_PATH;
            const qrLogoSrc = imagePath ? `${imagePath}images/logo-events-QRoption.png` : null;
            if (qrLogoSrc) {
                await drawRoundedLogoOnQrCanvas(canvas, qrLogoSrc);
            }

            let dataUrl;
            try {
                dataUrl = canvas.toDataURL('image/jpeg', 0.92);
            } catch {
                // Canvas « souillé » (CORS sur le logo) : réexport sans logo
                await QRCode.toCanvas(canvas, link, PURCHASE_QR_JPEG_OPTS);
                dataUrl = canvas.toDataURL('image/jpeg', 0.92);
            }

            const safeCode = (details?.event_code || 'event').toString().replace(/[^a-zA-Z0-9-_]/g, '-');
            const a = document.createElement('a');
            a.href = dataUrl;
            a.download = `qr-lien-achat-${safeCode}.jpg`;
            a.rel = 'noopener';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            setSuccessMessage('QR code téléchargé (JPEG).');
            setTimeout(() => setSuccessMessage(''), 4000);
        } catch {
            setValidationError('Impossible de générer le fichier JPEG.');
            setTimeout(() => setValidationError(''), 5000);
        } finally {
            setDownloadingPurchaseQr(false);
        }
    }, [details?.purchase_link, details?.event_code]);

    const closeModal = () => {
        setShareModalOpen(false);
    };

    const handleDeletePassClick = (data) => {
        setSelectedPass(data);
        setDeletePassModalOpen(true);
    };

    const closeDeletePassModal = () => {
        setDeletePassModalOpen(false);
        setSelectedPass(null);
    };

    const handleEditPassClick = (data) => {
        setSelectedPass(data);
        setEditPassModalOpen(true);
    };

    const closeEditPassModal = () => {
        setEditPassModalOpen(false);
        setSelectedPass(null);
    };

    const handleSuccess = (message) => {
        setSuccessMessage(message);
        setTimeout(() => setSuccessMessage(""), 5000);
        setDeletePassModalOpen(false);
        fetchEventDetails();
    };

    const handleToggleShowNbr = async (pivot, showNbr) => {
        if (togglingShowNbrPivotId === pivot.id) {
            return;
        }

        const previousValue = isShowNbrEnabled(pivot);
        setTogglingShowNbrPivotId(pivot.id);

        setDetails((prev) => {
            if (!prev) return prev;

            return {
                ...prev,
                pivots: prev.pivots.map((item) =>
                    item.id === pivot.id ? { ...item, show_nbr: showNbr } : item,
                ),
            };
        });

        try {
            const { data } = await axiosClient.post(
                `/request/user/organizer/event-official/ticket/toggle-show-nbr/pI/${pivot.id}`,
                { show_nbr: showNbr },
            );

            setValidationError('');
            setSuccessMessage(data.message || 'Paramètre mis à jour.');
            setTimeout(() => setSuccessMessage(''), 4000);
        } catch (err) {
            setDetails((prev) => {
                if (!prev) return prev;

                return {
                    ...prev,
                    pivots: prev.pivots.map((item) =>
                        item.id === pivot.id ? { ...item, show_nbr: previousValue } : item,
                    ),
                };
            });

            const message =
                err.response?.data?.message || 'Impossible de mettre à jour l\'affichage du stock.';
            setValidationError(message);
            setTimeout(() => setValidationError(''), 5000);
        } finally {
            setTogglingShowNbrPivotId(null);
        }
    };

    const totalViews = Number(details?.total_views ?? 0) || 0;

    const pivots = Array.isArray(details?.pivots) ? details.pivots : [];
    const isMultiSession = isMultiSessionEvent(details);

    useEffect(() => {
        if (!details || !isMultiSession) {
            setActiveTicketTab(null);
            return;
        }
        setActiveTicketTab(resolveInitialTicketTab(details, pivots));
    }, [details?.id, details?.is_multi_session, pivots.length]);

    const visiblePivots = useMemo(() => {
        if (!details || !isMultiSession || !activeTicketTab) return pivots;
        return ticketsForTab(pivots, activeTicketTab, details);
    }, [details, pivots, isMultiSession, activeTicketTab]);

    if(!id) {
        return (
            <div className="w-full h-full flex justify-center items-center cweej">
                <div className="cydwr clbq0">
                    <div className="inline-flex items-center justify-center rounded-full c1qph c3x05 c7rlo ce36h cbcgo cdiog c3nk1 crzrx">
                        <svg className="cbm9w ctt6r cye3x" viewBox="0 0 20 24" xmlns="http://www.w3.org/2000/svg" fill="none">
                            <path
                                className="text-gray-500 co1wq"
                                d="M10 10.562l9-5-8.514-4.73a1 1 0 00-.972 0L1 5.562l9 5z"
                                fill="currentColor"
                            />
                            <path
                                className="dark:text-gray-400 ckbo4"
                                d="M9 12.294l-9-5v10.412a1 1 0 00.514.874L9 23.294v-11z"
                                fill="currentColor"
                            />
                            <path
                                className="cmpw7 cdqku"
                                d="M11 12.294v11l8.486-4.714a1 1 0 00.514-.874V7.295l-9 4.999z"
                                fill="currentColor"
                            />
                        </svg>
                    </div>
                    <h4 className="text-gray-800 dark:text-gray-100 font-bold text-lg c6f83">
                    Aucun événement trouvé, impossible d'accéder à cette page.
                    </h4>
                    <Link to={'/dashboard/events/show-list'} className="btn bg-white border-gray-200 text-gray-800 cc0oq cghq3 cspbm c0zkc c2vpa">
                    Retour aux événements
                    </Link>
                </div>
            </div>
        )
    }

    if (loading) {
        return <LoadContentSpinner />;
    }

    // Afficher une section dédiée en cas d'erreur
    if (error) {
        return (
            <div className="w-full h-full flex justify-center items-center cweej">
                <div className="cydwr clbq0">
                    <div className="inline-flex items-center justify-center rounded-full c1qph c3x05 c7rlo ce36h cbcgo cdiog c3nk1 crzrx">
                        <svg className="cbm9w ctt6r cye3x" viewBox="0 0 20 24" xmlns="http://www.w3.org/2000/svg" fill="none">
                            <path
                                className="text-gray-500 co1wq"
                                d="M10 10.562l9-5-8.514-4.73a1 1 0 00-.972 0L1 5.562l9 5z"
                                fill="currentColor"
                            />
                            <path
                                className="dark:text-gray-400 ckbo4"
                                d="M9 12.294l-9-5v10.412a1 1 0 00.514.874L9 23.294v-11z"
                                fill="currentColor"
                            />
                            <path
                                className="cmpw7 cdqku"
                                d="M11 12.294v11l8.486-4.714a1 1 0 00.514-.874V7.295l-9 4.999z"
                                fill="currentColor"
                            />
                        </svg>
                    </div>
                    <h4 className="text-gray-800 dark:text-gray-100 font-bold text-lg c6f83">
                    OUPS !
                    </h4>
                    <div className="mb-6 text-sm">
                    {error.message}
                    </div>
                    {error.errorCode === 'EVENT_ACCESS_DENIED' && (
                        <Link to={'/dashboard/events/show-list'} className="btn bg-white border-gray-200 text-gray-800 cc0oq cghq3 cspbm c0zkc c2vpa">
                        Retour aux événements
                        </Link>
                    )}
                </div>
            </div>
        );
    }

    if (!details) {
        return (
            <div className="w-full h-full flex justify-center items-center cweej">
                <div className="cydwr clbq0">
                    <h4 className="text-gray-800 dark:text-gray-100 font-bold text-lg c6f83">
                        Aucun détail disponible.
                    </h4>
                    <Link
                        to="/dashboard/events/show-list"
                        className="btn bg-white border-gray-200 text-gray-800 cc0oq cghq3 cspbm c0zkc c2vpa"
                    >
                        Retour aux événements
                    </Link>
                </div>
            </div>
        );
    }

    /** URL affichée (résolue côté client) ; largeur du champ basée sur la longueur du texte. */
    const purchaseUrl = details.purchase_link || details.lien_achat;
    const purchaseLinkInputSize = Math.min(
        Math.max(String(purchaseUrl || '').length + 3, 22),
        110
    );

    const stockRemaining = pivots.reduce((sum, p) => sum + (Number(p.nbr_ticket) || 0), 0);
    const stockTotal = pivots.reduce((sum, p) => sum + (Number(p.total_billet) || 0), 0);
    const promoCount = Number(details.active_promotions_count) || 0;
    const maxPromoPct = pivots.reduce((max, p) => {
        const original = Number(p.promo_original_price);
        const promo = Number(p.promo_price);
        if (!p.has_promo || !Number.isFinite(original) || original <= 0 || !Number.isFinite(promo)) {
            return max;
        }
        const pct = Math.round(((original - promo) / original) * 100);
        return max === null || pct > max ? pct : max;
    }, null);

    const tabs = [
        { id: 'general', label: "Vue d'ensemble" },
        { id: 'billets', label: 'Billets & prix' },
        { id: 'diffusion', label: 'Diffusion' },
        { id: 'stats', label: 'Stats' },
        { id: 'Actions', label: 'Actions' },
    ];

    // Même sémantique que ListEvents : champ API `status` (string) + flag `annule`
    const isCancelled = Boolean(details.annule);
    const rawStatus = String(details.status || '').trim();
    const statusLabel = isCancelled
        ? 'Événement annulé'
        : rawStatus === 'Disponible'
          ? 'Disponible'
          : rawStatus === 'Disabled' || rawStatus.toLowerCase() === 'disabled'
            ? 'Désactivé ou passé'
            : rawStatus || '—';

    const statusTone = isCancelled
        ? 'bg-rose-500/15 text-rose-700 dark:text-rose-300 ring-rose-500/30'
        : rawStatus === 'Disponible'
          ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 ring-emerald-500/30'
          : rawStatus === 'Disabled' || rawStatus.toLowerCase() === 'disabled'
            ? 'bg-slate-500/15 text-slate-700 dark:text-slate-300 ring-slate-500/30'
            : 'bg-amber-500/15 text-amber-800 dark:text-amber-300 ring-amber-500/30';

    const posterSrc = (() => {
        if (details.poster) return details.poster;
        if (!details.affiche) return null;
        const affiche = String(details.affiche);
        return affiche.startsWith('data:') ? affiche : `data:image/jpeg;base64,${affiche}`;
    })();

    return (
        <>
            {validationError && (
                <div className="flex w-full justify-center items-center relative">
                    <div className="text-sm fixed mt-10 z-[200] px-3 rounded-lg bg-red-100 text-gray-700 shadow-md">
                        <div className="flex items-center gap-2 py-2">
                            <svg className="text-red-500 shrink-0" width={16} height={16} viewBox="0 0 16 16" fill="currentColor">
                                <path d="M8 0C3.6 0 0 3.6 0 8s3.6 8 8 8 8-3.6 8-8-3.6-8-8-8zm0 12c-.6 0-1-.4-1-1s.4-1 1-1 1 .4 1 1-.4 1-1 1zm1-3H7V4h2v5z" />
                            </svg>
                            <div>{validationError}</div>
                        </div>
                    </div>
                </div>
            )}
            {successMessage && (
                <div className="flex w-full justify-center items-center relative">
                    <div className="text-sm fixed mt-10 z-[200] px-3 rounded-lg bg-green-100 text-gray-700 shadow-md">
                        <div className="flex items-center gap-2 py-2">
                            <svg className="text-green-500 shrink-0" width={16} height={16} viewBox="0 0 16 16" fill="currentColor">
                                <path d="M8 0C3.6 0 0 3.6 0 8s3.6 8 8 8 8-3.6 8-8-3.6-8-8-8zM7 11.4L3.6 8 5 6.6l2 2 4-4L12.4 6 7 11.4z" />
                            </svg>
                            <div>{successMessage}</div>
                        </div>
                    </div>
                </div>
            )}

            <div className="relative px-4 sm:px-6 lg:px-8 py-6 sm:py-8 overflow-visible">
                {/* Atmosphere */}
                <div
                    aria-hidden
                    className="pointer-events-none absolute inset-0 -z-10"
                    style={{
                        background:
                            'radial-gradient(ellipse 80% 50% at 10% -10%, rgba(13,148,136,0.14), transparent 55%), radial-gradient(ellipse 60% 40% at 90% 0%, rgba(15,23,42,0.06), transparent 50%)',
                    }}
                />

                {/* Breadcrumb */}
                <div className="mb-5 flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400 animate-[fadeIn_0.4s_ease-out]">
                    <Link
                        to="/dashboard/events/show-list"
                        className="inline-flex items-center gap-1.5 hover:text-teal-700 dark:hover:text-teal-300 transition-colors"
                    >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                        </svg>
                        Événements
                    </Link>
                    <span className="text-slate-300 dark:text-slate-600">/</span>
                    <span className="text-slate-700 dark:text-slate-200 font-medium truncate max-w-[14rem] sm:max-w-md">
                        {details.event_title || 'Détail'}
                    </span>
                </div>

                {/* Hero cockpit */}
                <section className="relative z-20 rounded-2xl overflow-visible border border-slate-200/80 dark:border-slate-700/60 bg-white/80 dark:bg-slate-900/70 shadow-sm backdrop-blur-sm mb-5 animate-[fadeInUp_0.45s_ease-out]">
                    <div className="absolute inset-0 opacity-[0.04] dark:opacity-[0.07] pointer-events-none"
                        style={{
                            backgroundImage:
                                'linear-gradient(135deg, #0d9488 0%, transparent 40%), linear-gradient(225deg, #0f172a 0%, transparent 50%)',
                        }}
                    />
                    <div className="relative flex flex-col lg:flex-row gap-5 lg:gap-7 p-4 sm:p-5 lg:p-6">
                        {/* Poster */}
                        <div className="shrink-0 w-full sm:w-44 lg:w-48">
                            <div className="relative aspect-[3/4] sm:aspect-auto sm:h-56 lg:h-60 rounded-xl overflow-hidden bg-slate-100 dark:bg-slate-800 ring-1 ring-slate-200/80 dark:ring-slate-700 group">
                                {posterSrc ? (
                                    <>
                                        <button
                                            type="button"
                                            onClick={() => setPosterLightboxOpen(true)}
                                            className="absolute inset-0 z-[1] w-full h-full text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-inset"
                                            aria-label="Voir l'affiche en grand"
                                            title="Voir l'affiche en grand"
                                        >
                                            <img
                                                src={posterSrc}
                                                alt={details.event_title || 'Affiche'}
                                                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                                            />
                                            <span className="absolute inset-0 bg-black/0 group-hover:bg-black/25 transition-colors" />
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setPosterLightboxOpen(true)}
                                            className="absolute bottom-2 right-2 z-[2] inline-flex items-center gap-1.5 rounded-full bg-white/95 dark:bg-slate-900/90 text-slate-800 dark:text-slate-100 px-2.5 py-1.5 text-[11px] font-semibold shadow-md ring-1 ring-black/5 hover:bg-white dark:hover:bg-slate-800 transition-transform hover:scale-[1.03]"
                                            aria-label="Agrandir l'affiche"
                                            title="Agrandir"
                                        >
                                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5" />
                                            </svg>
                                            Agrandir
                                        </button>
                                    </>
                                ) : (
                                    <div className="absolute inset-0 flex items-center justify-center text-slate-400 text-sm">
                                        Pas d&apos;affiche
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Meta + CTAs */}
                        <div className="min-w-0 flex-1 flex flex-col">
                            <div className="flex flex-wrap items-center gap-2 mb-2">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ring-1 ${statusTone}`}>
                                    {statusLabel}
                                </span>
                                {details.event_code && (
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300">
                                        {details.event_code}
                                    </span>
                                )}
                                {details.has_active_promo && (
                                    <Link
                                        to={`/dashboard/communiquer/promotions/event/${details.id}`}
                                        className="inline-flex transition-transform hover:scale-[1.02]"
                                    >
                                        <PromoEventBadge
                                            discountPercent={maxPromoPct}
                                            className="static"
                                        />
                                    </Link>
                                )}
                            </div>

                            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 dark:text-white leading-tight mb-3">
                                {details.event_title || '—'}
                            </h1>

                            <div className="flex flex-wrap gap-x-4 gap-y-2 text-sm text-slate-600 dark:text-slate-300 mb-4">
                                {details.default_date && (
                                    <span className="inline-flex items-center gap-1.5">
                                        <svg className="w-4 h-4 text-teal-600 dark:text-teal-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                        </svg>
                                        {details.default_date}
                                        {details.other_date ? ` → ${details.other_date}` : ''}
                                        {details.hour ? ` · ${details.hour} GMT` : ''}
                                    </span>
                                )}
                                {details.place && (
                                    <span className="inline-flex items-center gap-1.5">
                                        <svg className="w-4 h-4 text-teal-600 dark:text-teal-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                        </svg>
                                        {details.place}
                                    </span>
                                )}
                                {details.eventcategorie?.name && (
                                    <span className="inline-flex items-center gap-1.5 text-slate-500 dark:text-slate-400">
                                        {details.eventcategorie.name}
                                    </span>
                                )}
                            </div>

                            <div className="mt-auto flex flex-wrap items-center gap-2 pt-1">
                                <button
                                    type="button"
                                    onClick={handleShareClick}
                                    className="inline-flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 transition-colors"
                                >
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                                    </svg>
                                    Partager
                                </button>

                                <Link
                                    to={`/dashboard/communiquer/promotions/event/${details.id}`}
                                    className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm font-semibold border transition-colors ${
                                        details.has_active_promo
                                            ? 'border-rose-200 bg-white text-rose-700 hover:bg-rose-50 dark:border-rose-500/30 dark:bg-slate-900 dark:text-rose-300 dark:hover:bg-rose-500/10'
                                            : 'border-gray-200 bg-white text-gray-800 hover:bg-gray-50 dark:border-gray-600 dark:bg-slate-800 dark:text-gray-200 dark:hover:bg-slate-700'
                                    }`}
                                >
                                    {details.has_active_promo ? (
                                        <>Promo active{promoCount > 1 ? ` · ${promoCount}` : ''}</>
                                    ) : (
                                        <>Créer une promo</>
                                    )}
                                </Link>

                                <Link
                                    to={`/dashboard/communiquer/liens-paiement/nouveau?event=${encodeURIComponent(details.id)}`}
                                    className="inline-flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm font-semibold border border-gray-200 bg-white text-gray-800 hover:bg-gray-50 dark:border-gray-600 dark:bg-slate-800 dark:text-gray-200 dark:hover:bg-slate-700 transition-colors"
                                >
                                    Lien de paiement
                                </Link>

                                {purchaseUrl && (
                                    <a
                                        href={purchaseUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm font-medium text-gray-800 dark:text-gray-200 border border-gray-200 dark:border-gray-600 bg-white dark:bg-slate-800 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
                                    >
                                        Voir la page publique
                                        <svg className="w-3.5 h-3.5 opacity-60" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                        </svg>
                                    </a>
                                )}

                                <div className="ml-auto relative z-30">
                                    <DropdownEditMenu align="right" className="relative inline-flex">
                                        <li>
                                            <button
                                                type="button"
                                                onClick={() => handleTabClick('diffusion')}
                                                className="font-medium text-sm text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-200 flex py-1 px-3 w-full text-left"
                                            >
                                                Lien & QR
                                            </button>
                                        </li>
                                        <li>
                                            <Link
                                                to={`/dashboard/ventes/tickets-vendus/e/${details.id}`}
                                                className="font-medium text-sm text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-200 flex py-1 px-3"
                                            >
                                                Ventes de l&apos;événement
                                            </Link>
                                        </li>
                                        <li>
                                            <Link
                                                to={`/dashboard/communiquer/promotions/event/${details.id}`}
                                                className="font-medium text-sm text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-200 flex py-1 px-3"
                                            >
                                                Gérer les promotions
                                            </Link>
                                        </li>
                                        <li>
                                            <Link
                                                to={`/dashboard/communiquer/liens-paiement/nouveau?event=${encodeURIComponent(details.id)}`}
                                                className="font-medium text-sm text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-200 flex py-1 px-3"
                                            >
                                                Lien de paiement
                                            </Link>
                                        </li>
                                        <li>
                                            <Link
                                                to="/dashboard/achats/events/validation/events-scan"
                                                className="font-medium text-sm text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-200 flex py-1 px-3"
                                            >
                                                Contrôle d&apos;accès (scan)
                                            </Link>
                                        </li>
                                        
                                        <hr className="my-1 border-gray-100 dark:border-gray-700" />
                                        <li>
                                            <Link
                                                to={`/dashboard/account/administration-organizer/contact/events-supprort-organizers?event_code=${encodeURIComponent(details.event_code || '')}&event_title=${encodeURIComponent(details.event_title || '')}`}
                                                className="font-medium text-sm text-red-600 dark:text-gray-300 hover:text-red-800 dark:hover:text-gray-200 flex py-1 px-3"
                                            >
                                                Assistance{details.event_code ? ` · ${details.event_code}` : ''}
                                            </Link>
                                        </li>
                                        {/* <li>
                                            <Link
                                                to="/dashboard/events/show-list"
                                                className="font-medium text-sm text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-200 flex py-1 px-3"
                                            >
                                                Retour à la liste
                                            </Link>
                                        </li> */}
                                    </DropdownEditMenu>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* KPI strip */}
                <section className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6 animate-[fadeInUp_0.55s_ease-out]">
                    <div className="rounded-xl border border-slate-200/80 dark:border-slate-700/60 bg-white/90 dark:bg-slate-900/60 px-4 py-3.5">
                        <p className="text-[11px] uppercase tracking-wider font-semibold text-slate-400 dark:text-slate-500 mb-1">
                            Vues
                        </p>
                        <p className="text-2xl font-bold tabular-nums text-slate-900 dark:text-white">
                            {formatPerformNumber(animatedViewsCount)}
                        </p>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                            {totalViews === 1 ? 'vue cumulée' : 'vues cumulées'}
                        </p>
                    </div>

                    <button
                        type="button"
                        onClick={() => handleTabClick('billets')}
                        className="text-left rounded-xl border border-slate-200/80 dark:border-slate-700/60 bg-white/90 dark:bg-slate-900/60 px-4 py-3.5 hover:border-slate-400/80 dark:hover:border-slate-600/50 transition-colors"
                    >
                        <p className="text-[11px] uppercase tracking-wider font-semibold text-slate-400 dark:text-slate-500 mb-1">
                            Stock
                        </p>
                        <p className="text-2xl font-bold tabular-nums text-slate-900 dark:text-white">
                            {formatNumber(stockRemaining)}
                            <span className="text-base font-semibold text-slate-400 dark:text-slate-500">
                                {' '}/ {formatNumber(stockTotal)}
                            </span>
                        </p>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                            places restantes
                        </p>
                    </button>

                    <Link
                        to={`/dashboard/communiquer/promotions/event/${details.id}`}
                        className="rounded-xl border border-slate-200/80 dark:border-slate-700/60 bg-white/90 dark:bg-slate-900/60 px-4 py-3.5 hover:border-slate-400/80 dark:hover:border-slate-600/40 transition-colors"
                    >
                        <p className="text-[11px] uppercase tracking-wider font-semibold text-slate-400 dark:text-slate-500 mb-1">
                            Promo
                        </p>
                        {details.has_active_promo ? (
                            <>
                                <p className="text-2xl font-bold text-amber-700 dark:text-amber-300">
                                    {maxPromoPct != null ? `−${maxPromoPct}%` : 'Active'}
                                </p>
                                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                                    {promoCount} promo{promoCount > 1 ? 's' : ''} · gérer
                                </p>
                            </>
                        ) : (
                            <>
                                <p className="text-2xl font-bold text-slate-400 dark:text-slate-500">—</p>
                                <p className="text-xs text-teal-700 dark:text-teal-400 mt-0.5 font-medium">
                                    Créer une promo
                                </p>
                            </>
                        )}
                    </Link>

                    <button
                        type="button"
                        onClick={() => handleTabClick('stats')}
                        className="text-left rounded-xl border border-slate-200/80 dark:border-slate-700/60 bg-white/90 dark:bg-slate-900/60 px-4 py-3.5 hover:border-slate-400/80 dark:hover:border-slate-600/50 transition-colors"
                    >
                        <p className="text-[11px] uppercase tracking-wider font-semibold text-slate-400 dark:text-slate-500 mb-1">
                            Performance
                        </p>
                        <p className="text-lg font-bold text-teal-700 dark:text-teal-300">
                            Voir les stats
                        </p>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                            commandes & billetterie
                        </p>
                    </button>
                </section>

                {/* Tabs */}
                <div className="mb-5 border-b border-slate-200 dark:border-slate-700/80 overflow-x-auto">
                    <nav className="flex gap-1 min-w-max" aria-label="Onglets détail événement">
                        {tabs.map((tab) => {
                            const active = activeTab === tab.id;
                            return (
                                <button
                                    key={tab.id}
                                    type="button"
                                    onClick={() => handleTabClick(tab.id)}
                                    className={`relative px-3.5 sm:px-4 py-2.5 text-sm font-semibold whitespace-nowrap transition-colors ${
                                        active
                                            ? 'text-teal-700 dark:text-teal-300'
                                            : 'text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200'
                                    }`}
                                >
                                    {tab.label}
                                    {tab.id === 'billets' && pivots.length > 0 && (
                                        <span className={`ml-1.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                                            active
                                                ? 'bg-teal-100 text-teal-800 dark:bg-teal-500/20 dark:text-teal-200'
                                                : 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400'
                                        }`}>
                                            {pivots.length}
                                        </span>
                                    )}
                                    {active && (
                                        <span className="absolute left-2 right-2 -bottom-px h-0.5 rounded-full bg-teal-600 dark:bg-teal-400" />
                                    )}
                                </button>
                            );
                        })}
                    </nav>
                </div>

                {/* Tab panels */}
                <div className="animate-[fadeIn_0.35s_ease-out]">
                    {activeTab === 'general' && (
                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
                            <div className="lg:col-span-7 space-y-5">
                                <div className="rounded-xl border border-slate-200/80 dark:border-slate-700/60 bg-white/90 dark:bg-slate-900/50 p-5">
                                    <div className="flex items-center justify-between gap-3 mb-3">
                                        <h2 className="text-base font-bold text-slate-900 dark:text-white">
                                            Description
                                        </h2>
                                        <button
                                            type="button"
                                            onClick={() => setIsEditing(!isEditing)}
                                            className="inline-flex items-center gap-1.5 text-xs font-semibold text-teal-700 dark:text-teal-300 hover:underline"
                                        >
                                            <svg className="w-3.5 h-3.5" width={14} height={14} viewBox="0 0 16 16" fill="currentColor">
                                                <path d="M11.7.3c-.4-.4-1-.4-1.4 0l-10 10c-.2.2-.3.4-.3.7v4c0 .6.4 1 1 1h4c.3 0 .5-.1.7-.3l10-10c.4-.4.4-1 0-1.4l-4-4zM4.6 14H2v-2.6l6-6L10.6 8l-6 6zM12 6.6L9.4 4 11 2.4 13.6 5 12 6.6z" />
                                            </svg>
                                            {isEditing ? 'Fermer' : 'Modifier'}
                                        </button>
                                    </div>
                                    {isEditing ? (
                                        <div>
                                            <textarea
                                                id="comment"
                                                className="cn9r8 c071z w-full mb-3"
                                                rows={5}
                                                value={description}
                                                onChange={(e) => setDescription(e.target.value)}
                                                required
                                            />
                                            {errorsDesc && (
                                                <small className="text-red-600 block mb-2">{errorsDesc}</small>
                                            )}
                                            <div className="flex flex-wrap justify-end gap-2">
                                                <button
                                                    type="button"
                                                    onClick={handleCancel}
                                                    className="border border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-200 px-3 py-1.5 rounded-lg text-sm"
                                                >
                                                    Annuler
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={handleSave}
                                                    disabled={loadingBtnDesc}
                                                    className="bg-teal-600 hover:bg-teal-700 text-white px-3 py-1.5 rounded-lg text-sm font-semibold disabled:opacity-60 inline-flex items-center"
                                                >
                                                    {loadingBtnDesc ? 'Sauvegarde...' : 'Enregistrer'}
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <p className="text-sm text-slate-600 dark:text-slate-300 whitespace-pre-wrap leading-relaxed">
                                            {details.descrip_apk || 'Aucune description.'}
                                        </p>
                                    )}
                                </div>

                                {(details.populaire === 1 || details.vedette === 1) && (
                                    <div className="flex flex-wrap gap-2">
                                        {details.populaire === 1 && (
                                            <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-teal-50 text-teal-800 dark:bg-teal-500/15 dark:text-teal-200">
                                                Populaire
                                            </span>
                                        )}
                                        {details.vedette === 1 && (
                                            <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200">
                                                Vedette
                                            </span>
                                        )}
                                    </div>
                                )}
                            </div>

                            <aside className="lg:col-span-5 space-y-0 rounded-xl border border-slate-200/80 dark:border-slate-700/60 bg-white/90 dark:bg-slate-900/50 divide-y divide-slate-100 dark:divide-slate-800 overflow-hidden">
                                {[
                                    { label: 'Code', value: details.event_code },
                                    { label: 'Début', value: details.default_date },
                                    ...(details.other_date ? [{ label: 'Fin', value: details.other_date }] : []),
                                    { label: 'Heure', value: details.hour ? `${details.hour} GMT` : null },
                                    { label: 'Lieu', value: details.place },
                                    {
                                        label: 'Clôture des ventes',
                                        value: details.cloture_vente ? (
                                            <FormattedDate date={details.cloture_vente} />
                                        ) : null,
                                    },
                                    { label: 'Lien GPS', value: details.events_request?.lien_gps },
                                    { label: 'Acteur', value: details.artist },
                                    { label: 'Catégorie', value: details.eventcategorie?.name },
                                    {
                                        label: 'Créé le',
                                        value: details.created_at ? (
                                            <FormattedDate date={details.created_at} />
                                        ) : null,
                                    },
                                ].map((row) => (
                                    <div key={row.label} className="px-4 py-3 flex gap-3 text-sm">
                                        <dt className="w-36 shrink-0 font-medium text-slate-500 dark:text-slate-400">
                                            {row.label}
                                        </dt>
                                        <dd className="min-w-0 text-slate-800 dark:text-slate-100 break-words">
                                            {row.value || '—'}
                                        </dd>
                                    </div>
                                ))}
                            </aside>
                        </div>
                    )}

                    {activeTab === 'billets' && (
                        <div>
                            <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
                                <div>
                                    <h2 className="text-lg font-bold text-slate-900 dark:text-white">
                                        Billets & tarifs
                                    </h2>
                                    <p className="text-sm text-slate-500 dark:text-slate-400">
                                        Stock, prix promo et affichage public des places restantes.
                                    </p>
                                </div>
                                {details.has_active_promo && (
                                    <DashboardPromoMarker
                                        eventId={details.id}
                                        hasActivePromo={details.has_active_promo}
                                        activeCount={details.active_promotions_count}
                                    />
                                )}
                            </div>

                            {pivots.length === 0 ? (
                                <div className="rounded-xl border border-dashed border-slate-300 dark:border-slate-600 px-6 py-12 text-center text-sm text-slate-500">
                                    Aucun billet configuré pour cet événement.
                                </div>
                            ) : (
                                <>
                                    {isMultiSession && (
                                        <PassSessionTabs
                                            event={details}
                                            tickets={pivots}
                                            activeTab={activeTicketTab}
                                            onChange={setActiveTicketTab}
                                        />
                                    )}
                                    {visiblePivots.length === 0 ? (
                                        <div className="rounded-xl border border-dashed border-slate-300 dark:border-slate-600 px-6 py-10 text-center text-sm text-slate-500">
                                            Aucun billet pour cette sélection.
                                        </div>
                                    ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                                    {visiblePivots.map((data) => {
                                        const isSoldOut = () => data.nbr_ticket === 0;
                                        // API : `price` (effectif) ; fallback catalogue `prix_unitaire`
                                        const displayPrice = Number(
                                            data.price ?? data.prix_unitaire ?? 0,
                                        );
                                        const originalPrice = Number(
                                            data.promo_original_price ??
                                                data.price ??
                                                data.prix_unitaire,
                                        );
                                        const promoPrice = Number(
                                            data.promo_price ?? data.price ?? data.prix_unitaire,
                                        );
                                        const showPromo =
                                            Boolean(data.has_promo) &&
                                            Number.isFinite(originalPrice) &&
                                            Number.isFinite(promoPrice) &&
                                            promoPrice < originalPrice;
                                        const discountPct = showPromo
                                            ? Math.round(
                                                  ((originalPrice - promoPrice) / originalPrice) * 100,
                                              )
                                            : null;

                                        return (
                                            <div
                                                key={data.id}
                                                className="relative z-0 hover:z-30 focus-within:z-30 rounded-xl border border-slate-200/80 dark:border-slate-700/60 bg-white dark:bg-slate-900/60 p-4 flex flex-col transition-shadow hover:shadow-md overflow-visible"
                                            >
                                                <div className="flex items-start justify-between gap-2 mb-3">
                                                    <div className="min-w-0">
                                                        <h3 className="font-semibold text-slate-900 dark:text-white truncate">
                                                            {data.findthislabel?.title || 'Billet'}
                                                        </h3>
                                                        {isMultiSession && (
                                                            <div className="mt-1">
                                                                <SessionBadge ticket={data} />
                                                            </div>
                                                        )}
                                                        {showPromo ? (
                                                            <div className="mt-1 flex flex-wrap items-baseline gap-2">
                                                                <span className="text-lg font-bold text-teal-700 dark:text-teal-300">
                                                                    {formatPrice(promoPrice)}
                                                                </span>
                                                                <span className="text-sm text-slate-400 line-through">
                                                                    {formatPrice(originalPrice)}
                                                                </span>
                                                                {discountPct != null && (
                                                                    <span className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-200">
                                                                        −{discountPct}%
                                                                    </span>
                                                                )}
                                                            </div>
                                                        ) : (
                                                            <p className="mt-1 text-lg font-bold text-slate-800 dark:text-slate-100">
                                                                {formatPrice(displayPrice)}
                                                            </p>
                                                        )}
                                                    </div>
                                                    <span
                                                        className={`shrink-0 text-[11px] font-semibold px-2 py-0.5 rounded-full ${
                                                            isSoldOut()
                                                                ? 'bg-rose-500 text-white'
                                                                : 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300'
                                                        }`}
                                                    >
                                                        {isSoldOut() ? 'Sold out' : 'En stock'}
                                                    </span>
                                                </div>

                                                <div className="flex items-center justify-between text-sm mb-3 pb-3 border-b border-slate-100 dark:border-slate-800">
                                                    <span className="text-slate-500 dark:text-slate-400">
                                                        Restant
                                                    </span>
                                                    <span className="font-semibold tabular-nums text-slate-800 dark:text-slate-100">
                                                        {formatNumber(data.nbr_ticket)} /{' '}
                                                        {formatNumber(data.total_billet)}
                                                    </span>
                                                </div>

                                                <div className="flex items-center justify-between gap-3 mt-auto">
                                                    <div className="min-w-0">
                                                        <p className="text-xs font-medium text-slate-700 dark:text-slate-300">
                                                            Afficher le stock
                                                        </p>
                                                        <p className="text-[11px] text-slate-500">
                                                            Sur la billetterie publique
                                                        </p>
                                                    </div>
                                                    <div className="flex items-center shrink-0 gap-2">
                                                        <span className="text-xs text-slate-500 min-w-[3rem] text-right">
                                                            {togglingShowNbrPivotId === data.id
                                                                ? '…'
                                                                : isShowNbrEnabled(data)
                                                                  ? 'Activé'
                                                                  : 'Masqué'}
                                                        </span>
                                                        <button
                                                            type="button"
                                                            role="switch"
                                                            aria-checked={isShowNbrEnabled(data)}
                                                            aria-label={`Afficher le nombre de places pour ${data.findthislabel?.title || 'ce billet'}`}
                                                            disabled={togglingShowNbrPivotId === data.id}
                                                            onClick={() =>
                                                                handleToggleShowNbr(
                                                                    data,
                                                                    !isShowNbrEnabled(data),
                                                                )
                                                            }
                                                            className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500/40 ${
                                                                isShowNbrEnabled(data)
                                                                    ? 'bg-teal-600'
                                                                    : 'bg-slate-200 dark:bg-slate-700'
                                                            } ${
                                                                togglingShowNbrPivotId === data.id
                                                                    ? 'opacity-60 cursor-wait'
                                                                    : 'cursor-pointer'
                                                            }`}
                                                        >
                                                            <span
                                                                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                                                                    isShowNbrEnabled(data)
                                                                        ? 'translate-x-5'
                                                                        : 'translate-x-0.5'
                                                                }`}
                                                            />
                                                        </button>
                                                    </div>
                                                </div>

                                                {data.comment && (
                                                    <div className="relative z-20 flex items-center mt-3 pt-3 border-t border-slate-100 dark:border-slate-800 overflow-visible">
                                                        <CommentPassShow
                                                            comment={data.comment}
                                                            title={data.findthislabel?.title || 'Billet'}
                                                        />
                                                        <DropdownEditMenu
                                                            align="right"
                                                            className="relative inline-flex bg-white dark:bg-slate-900 cspbm"
                                                        >
                                                            <li>
                                                                <Link
                                                                    onClick={() => handleEditPassClick(data)}
                                                                    className="font-medium text-sm text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-200 flex py-1 px-3"
                                                                    to="#0"
                                                                >
                                                                    Modifier
                                                                </Link>
                                                            </li>
                                                            <hr />
                                                            <li>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleDeletePassClick(data)}
                                                                    className="font-medium text-sm text-red-500 hover:text-red-600 flex py-1 px-3"
                                                                >
                                                                    Le Supprimer
                                                                </button>
                                                            </li>
                                                        </DropdownEditMenu>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                                    )}
                                </>
                            )}
                        </div>
                    )}

                    {activeTab === 'diffusion' && (
                        <div className="max-w-3xl space-y-5">
                            <div>
                                <h2 className="text-lg font-bold text-slate-900 dark:text-white">
                                    Diffusion
                                </h2>
                                <p className="text-sm text-slate-500 dark:text-slate-400">
                                    Lien d&apos;achat public et QR code JPEG pour vos supports.
                                </p>
                            </div>

                            <div className="rounded-xl border border-slate-200/80 dark:border-slate-700/60 bg-white/90 dark:bg-slate-900/50 p-5 space-y-4">
                                <div>
                                    <label
                                        htmlFor="event-purchase-link"
                                        className="block text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2"
                                    >
                                        Lien d&apos;achat
                                    </label>
                                    {purchaseUrl ? (
                                        <div className="flex flex-col sm:flex-row gap-2">
                                            <input
                                                id="event-purchase-link"
                                                type="text"
                                                readOnly
                                                value={purchaseUrl}
                                                size={purchaseLinkInputSize}
                                                onFocus={(e) => e.target.select()}
                                                className="flex-1 min-w-0 text-sm rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-800/80 text-slate-800 dark:text-slate-100 px-3 py-2.5"
                                            />
                                            <div className="flex flex-wrap items-center gap-2 shrink-0">
                                                <button
                                                    type="button"
                                                    onClick={() => handleCopyPurchaseLink(purchaseUrl)}
                                                    title={copiedPurchaseLink ? 'Lien copié' : 'Copier le lien'}
                                                    aria-label={copiedPurchaseLink ? 'Lien copié' : 'Copier le lien'}
                                                    className={`inline-flex items-center justify-center h-10 w-10 rounded-lg border transition-colors ${
                                                        copiedPurchaseLink
                                                            ? 'border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-500/40 dark:bg-emerald-500/10 dark:text-emerald-300'
                                                            : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-slate-800 dark:text-gray-200 dark:hover:bg-slate-700'
                                                    }`}
                                                >
                                                    {copiedPurchaseLink ? (
                                                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                                        </svg>
                                                    ) : (
                                                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                                                            <path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                                        </svg>
                                                    )}
                                                </button>

                                                <button
                                                    type="button"
                                                    onClick={handleDownloadPurchaseQr}
                                                    disabled={downloadingPurchaseQr}
                                                    className="inline-flex items-center gap-1.5 px-3 py-2 h-10 rounded-lg text-sm font-semibold border border-gray-200 dark:border-gray-600 text-gray-800 dark:text-gray-200 bg-white dark:bg-slate-800 hover:bg-gray-50 dark:hover:bg-slate-700 disabled:opacity-60"
                                                >
                                                    {downloadingPurchaseQr ? 'Génération…' : 'QR JPEG'}
                                                </button>

                                                <div className="relative" ref={purchaseActionsRef}>
                                                    <button
                                                        type="button"
                                                        onClick={() => setPurchaseActionsOpen((open) => !open)}
                                                        aria-haspopup="menu"
                                                        aria-expanded={purchaseActionsOpen}
                                                        className="inline-flex items-center gap-1.5 px-3 py-2 h-10 rounded-lg text-sm font-semibold border border-gray-200 dark:border-gray-600 text-gray-800 dark:text-gray-200 bg-white dark:bg-slate-800 hover:bg-gray-50 dark:hover:bg-slate-700"
                                                    >
                                                        Plus
                                                        <svg className="w-4 h-4 opacity-70" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                                                        </svg>
                                                    </button>
                                                    {purchaseActionsOpen && (
                                                        <div
                                                            role="menu"
                                                            className="absolute right-0 top-full mt-1 z-[120] min-w-[11rem] rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 py-1.5 shadow-lg"
                                                        >
                                                            <a
                                                                role="menuitem"
                                                                href={purchaseUrl}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                onClick={() => setPurchaseActionsOpen(false)}
                                                                className="font-medium text-sm text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-200 flex py-1.5 px-3"
                                                            >
                                                                Ouvrir
                                                            </a>
                                                            <button
                                                                role="menuitem"
                                                                type="button"
                                                                onClick={() => {
                                                                    setPurchaseActionsOpen(false);
                                                                    handleShareClick();
                                                                }}
                                                                className="font-medium text-sm text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-200 flex py-1.5 px-3 w-full text-left"
                                                            >
                                                                Partager
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <p className="text-sm text-slate-500">Lien non disponible.</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'stats' && (
                        <EventsRecapOrdersComponent event={id} eventCode={details.event_code} />
                    )}

                    {activeTab === 'Actions' && <DoEventActions event={id} />}
                </div>
            </div>

            {isPosterLightboxOpen && posterSrc && (
                <div
                    className="fixed inset-0 z-[300] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 sm:p-6"
                    role="presentation"
                    onClick={() => setPosterLightboxOpen(false)}
                >
                    <div
                        role="dialog"
                        aria-modal="true"
                        aria-label="Affiche de l'événement en grand"
                        className="relative w-full max-w-3xl max-h-[90vh] flex flex-col"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="mb-3 flex items-center justify-between gap-3">
                            <p className="min-w-0 truncate text-sm font-medium text-white/90">
                                {details.event_title || 'Affiche de l\'événement'}
                            </p>
                            <button
                                type="button"
                                onClick={() => setPosterLightboxOpen(false)}
                                className="shrink-0 inline-flex items-center justify-center rounded-full bg-white/15 hover:bg-white/25 text-white w-9 h-9 transition-colors"
                                aria-label="Fermer"
                            >
                                <svg width={16} height={16} viewBox="0 0 16 16" fill="currentColor">
                                    <path d="M7.95 6.536l4.242-4.243a1 1 0 111.415 1.414L9.364 7.95l4.243 4.242a1 1 0 11-1.415 1.415L7.95 9.364l-4.243 4.243a1 1 0 01-1.414-1.415L6.536 7.95 2.293 3.707a1 1 0 011.414-1.414L7.95 6.536z" />
                                </svg>
                            </button>
                        </div>
                        <div className="flex-1 min-h-0 rounded-xl overflow-hidden bg-black/40 ring-1 ring-white/10">
                            <img
                                src={posterSrc}
                                alt={details.event_title || 'Affiche'}
                                className="w-full h-full max-h-[80vh] object-contain"
                            />
                        </div>
                    </div>
                </div>
            )}

            <ShareEventModal event={details} isOpen={isShareModalOpen} onClose={closeModal} />

            <ModalEditPassComment
                dataPass={selectedPass}
                isOpen={isEditPassModalOpen}
                onClose={closeEditPassModal}
                onSuccess={handleSuccess}
            />

            <ModalDeletePassComment
                data={selectedPass}
                isOpen={isDeletePassModalOpen}
                onClose={closeDeletePassModal}
                onSuccess={handleSuccess}
            />

            <style>{`
                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                @keyframes fadeInUp {
                    from { opacity: 0; transform: translateY(8px); }
                    to { opacity: 1; transform: translateY(0); }
                }
            `}</style>
        </>
    );
}
